/**
 * Camera 360° asset hotspots — types, validation, and API wrappers
 * (Administration → Camera management).
 */

import {
  createCameraHotspot,
  deleteCameraHotspotApi,
  fetchCameraHotspots,
  resolveCameraHotspotMediaUrl,
  updateCameraHotspot,
} from "./cameraHotspotsApi.ts";

export {
  fetchCameraHotspots,
  resolveCameraHotspotMediaUrl,
} from "./cameraHotspotsApi.ts";

export const CAMERA_ASSET_HOTSPOT_SHOW_TYPES = [
  "image",
  "pdf",
  "website",
  "youtube",
  "camera",
  "text",
  "api_url",
] as const;

export const HOTSPOT_TEXT_MAX_CHARS = 1024;
export const HOTSPOT_CONTENT_URL_MAX_CHARS = 1024;

/** Patrol / admin signal colors for API URL hotspots. */
export const API_URL_HOTSPOT_LANE_TYPES = ["green", "red", "yellow"] as const;

export type ApiUrlHotspotLaneType =
  (typeof API_URL_HOTSPOT_LANE_TYPES)[number];

export const API_URL_HOTSPOT_LANE_LABELS: Record<ApiUrlHotspotLaneType, string> =
  {
    green: "Green",
    red: "Red",
    yellow: "Yellow",
  };

export type ApiUrlHotspotEndpoints = Record<ApiUrlHotspotLaneType, string>;

export type ApiUrlHotspotEntry = {
  id: string;
  type: ApiUrlHotspotLaneType;
  url: string;
};

export const EMPTY_API_URL_ENDPOINTS: ApiUrlHotspotEndpoints = {
  green: "",
  red: "",
  yellow: "",
};

export function validateHotspotApiUrl(url: string): string | null {
  const s = String(url ?? "").trim();
  if (!s) return null;
  if (s.length > HOTSPOT_CONTENT_URL_MAX_CHARS) {
    return `Each API URL must be at most ${HOTSPOT_CONTENT_URL_MAX_CHARS} characters.`;
  }
  try {
    const parsed = new URL(s);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return "API URL must use http or https.";
    }
  } catch {
    return "Enter a valid API URL.";
  }
  return null;
}

const LANE_PARSE_KEYS: Record<ApiUrlHotspotLaneType, string[]> = {
  green: [
    "green",
    "Green",
    "g",
    "green_url",
    "greenUrl",
    "green_api",
    "greenApi",
  ],
  red: ["red", "Red", "r", "red_url", "redUrl", "red_api", "redApi"],
  yellow: [
    "yellow",
    "Yellow",
    "y",
    "yellow_url",
    "yellowUrl",
    "yellow_api",
    "yellowApi",
  ],
};

function readApiUrlField(
  o: Record<string, unknown>,
  keys: string[],
): string {
  for (const key of keys) {
    const v = o[key];
    if (v != null && String(v).trim()) {
      return String(v).trim();
    }
  }
  return "";
}

function parseApiUrlHotspotJsonObject(
  jsonText: string,
): ApiUrlHotspotEndpoints | null {
  try {
    let parsed: unknown = JSON.parse(jsonText);
    if (typeof parsed === "string" && parsed.trim().startsWith("{")) {
      parsed = JSON.parse(parsed.trim());
    }
    const o =
      parsed && typeof parsed === "object"
        ? (parsed as Record<string, unknown>)
        : {};

    if (Array.isArray(o.entries)) {
      const out = { ...EMPTY_API_URL_ENDPOINTS };
      for (const item of o.entries) {
        if (!item || typeof item !== "object") continue;
        const row = item as Record<string, unknown>;
        const type = String(row.type ?? row.color ?? "")
          .trim()
          .toLowerCase() as ApiUrlHotspotLaneType;
        const url = String(row.url ?? row.href ?? "").trim();
        if (API_URL_HOTSPOT_LANE_TYPES.includes(type) && url) {
          out[type] = url;
        }
      }
      return out;
    }

    return {
      green: readApiUrlField(o, LANE_PARSE_KEYS.green),
      red: readApiUrlField(o, LANE_PARSE_KEYS.red),
      yellow: readApiUrlField(o, LANE_PARSE_KEYS.yellow),
    };
  } catch {
    return null;
  }
}

export function parseApiUrlHotspotContent(
  raw: string | undefined | null,
): ApiUrlHotspotEndpoints {
  let s = String(raw ?? "").trim();
  if (!s) return { ...EMPTY_API_URL_ENDPOINTS };

  if (s.includes("%7B") || s.includes("%22")) {
    try {
      s = decodeURIComponent(s);
    } catch {
      /* keep original */
    }
  }

  if (s.startsWith("{")) {
    return parseApiUrlHotspotJsonObject(s) ?? { ...EMPTY_API_URL_ENDPOINTS };
  }

  const brace = s.indexOf("{");
  if (brace >= 0) {
    const embedded = parseApiUrlHotspotJsonObject(s.slice(brace));
    if (embedded && API_URL_HOTSPOT_LANE_TYPES.some((t) => embedded[t])) {
      return embedded;
    }
  }

  if (/^https?:\/\//i.test(s)) {
    return { ...EMPTY_API_URL_ENDPOINTS, green: s };
  }

  return { ...EMPTY_API_URL_ENDPOINTS };
}

export function getApiUrlHotspotLaneUrl(
  raw: string | undefined | null,
  lane: ApiUrlHotspotLaneType,
): string {
  return parseApiUrlHotspotContent(raw)[lane] ?? "";
}

export function getConfiguredApiUrlHotspotLanes(
  raw: string | undefined | null,
): ApiUrlHotspotLaneType[] {
  const endpoints = parseApiUrlHotspotContent(raw);
  return API_URL_HOTSPOT_LANE_TYPES.filter((t) => Boolean(endpoints[t]));
}

export function apiUrlHotspotEntriesFromContent(
  raw: string | undefined | null,
): ApiUrlHotspotEntry[] {
  const endpoints = parseApiUrlHotspotContent(raw);
  const entries: ApiUrlHotspotEntry[] = [];
  for (const type of API_URL_HOTSPOT_LANE_TYPES) {
    if (endpoints[type]) {
      entries.push({ id: `${type}-${entries.length}`, type, url: endpoints[type] });
    }
  }
  if (entries.length === 0) {
    return [{ id: "lane-0", type: "green", url: "" }];
  }
  return entries;
}

export function serializeApiUrlHotspotEntries(
  entries: ApiUrlHotspotEntry[],
): string {
  const endpoints = { ...EMPTY_API_URL_ENDPOINTS };
  for (const row of entries) {
    const url = String(row.url ?? "").trim();
    if (url) {
      endpoints[row.type] = url;
    }
  }
  const serialized = JSON.stringify(endpoints);
  if (serialized.length > HOTSPOT_CONTENT_URL_MAX_CHARS) {
    throw new Error(
      `API URLs are too long combined (max ${HOTSPOT_CONTENT_URL_MAX_CHARS} characters).`,
    );
  }
  return serialized;
}

/** @deprecated Use serializeApiUrlHotspotEntries */
export function serializeApiUrlHotspotContent(
  green: string,
  red: string,
  yellow = "",
): string {
  return serializeApiUrlHotspotEntries([
    { id: "g", type: "green", url: green },
    { id: "r", type: "red", url: red },
    ...(String(yellow ?? "").trim()
      ? [{ id: "y", type: "yellow" as const, url: yellow }]
      : []),
  ]);
}

export function validateApiUrlHotspotEntries(
  entries: ApiUrlHotspotEntry[],
): string | null {
  const filled = entries.filter((r) => String(r.url ?? "").trim());
  if (filled.length === 0) {
    return "Add at least one API URL.";
  }

  const typesSeen = new Set<string>();
  const urlsSeen = new Set<string>();

  for (const row of filled) {
    const type = row.type;
    if (typesSeen.has(type)) {
      return `Duplicate URL type "${API_URL_HOTSPOT_LANE_LABELS[type]}". Use each type once.`;
    }
    typesSeen.add(type);

    const url = String(row.url ?? "").trim();
    const ve = validateHotspotApiUrl(url);
    if (ve) {
      return `${API_URL_HOTSPOT_LANE_LABELS[type]}: ${ve}`;
    }

    const key = url.toLowerCase();
    if (urlsSeen.has(key)) {
      return "Each API URL must be different.";
    }
    urlsSeen.add(key);
  }

  try {
    serializeApiUrlHotspotEntries(entries);
  } catch (e) {
    return e instanceof Error ? e.message : "API URLs are too long.";
  }
  return null;
}

/** @deprecated Use validateApiUrlHotspotEntries */
export function validateApiUrlHotspotPair(
  green: string,
  red: string,
): string | null {
  return validateApiUrlHotspotEntries([
    { id: "g", type: "green", url: green },
    { id: "r", type: "red", url: red },
  ]);
}

export function nextAvailableApiUrlLaneType(
  entries: ApiUrlHotspotEntry[],
): ApiUrlHotspotLaneType | null {
  const used = new Set(entries.map((e) => e.type));
  return API_URL_HOTSPOT_LANE_TYPES.find((t) => !used.has(t)) ?? null;
}

export type CameraAssetHotspotShowType =
  (typeof CAMERA_ASSET_HOTSPOT_SHOW_TYPES)[number];

export const HOTSPOT_SHOW_TYPE_LABELS: Record<
  CameraAssetHotspotShowType,
  string
> = {
  image: "Image",
  pdf: "PDF",
  website: "Website",
  youtube: "YouTube",
  camera: "Camera",
  text: "Text",
  api_url: "API URL",
};

export function getHotspotShowTypeLabel(
  showType: string | undefined | null,
): string {
  const key = String(showType ?? "")
    .trim()
    .toLowerCase() as CameraAssetHotspotShowType;
  return HOTSPOT_SHOW_TYPE_LABELS[key] ?? (String(showType ?? "").trim() || "—");
}

export type CameraAssetHotspot = {
  id: string;
  camera_id: string;
  yaw_deg: number;
  pitch_deg: number;
  pin_direction_deg: number;
  asset_name: string;
  show_type: CameraAssetHotspotShowType;
  content_url: string;
  image_url?: string;
};

export type CameraAssetHotspotInput = Omit<
  CameraAssetHotspot,
  "id" | "camera_id"
>;

export async function listCameraHotspots(
  cameraId: string,
): Promise<CameraAssetHotspot[]> {
  return fetchCameraHotspots(cameraId);
}

export async function saveCameraHotspot(
  cameraId: string,
  input: CameraAssetHotspotInput,
  existingId?: string,
  file?: File | null,
): Promise<CameraAssetHotspot> {
  const cid = String(cameraId || "").trim();
  if (!cid) throw new Error("Camera id is required.");

  if (existingId?.trim()) {
    return updateCameraHotspot(cid, existingId.trim(), input, file);
  }
  return createCameraHotspot(cid, input, file);
}

export async function deleteCameraHotspot(
  cameraId: string,
  hotspotId: string,
): Promise<void> {
  return deleteCameraHotspotApi(cameraId, hotspotId);
}

export async function countCameraHotspots(cameraId: string): Promise<number> {
  const list = await fetchCameraHotspots(cameraId);
  return list.length;
}

export const HOTSPOT_IMAGE_MAX_BYTES = 50 * 1024 * 1024;
export const HOTSPOT_PDF_MAX_BYTES = 150 * 1024 * 1024;

const IMAGE_EXT = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp"];
const PDF_EXT = [".pdf"];

export function validateHotspotUploadFile(
  file: File,
  showType: "image" | "pdf",
): string | null {
  if (!file) return "No file selected.";
  const name = file.name.toLowerCase();
  if (showType === "image") {
    if (file.size > HOTSPOT_IMAGE_MAX_BYTES) {
      return `Image must be under ${HOTSPOT_IMAGE_MAX_BYTES / (1024 * 1024)}MB.`;
    }
    const ok =
      file.type.startsWith("image/") ||
      IMAGE_EXT.some((ext) => name.endsWith(ext));
    if (!ok) return "Upload a JPG, PNG, WEBP, or GIF image.";
    return null;
  }
  if (file.size > HOTSPOT_PDF_MAX_BYTES) {
    return `PDF must be under ${HOTSPOT_PDF_MAX_BYTES / (1024 * 1024)}MB.`;
  }
  if (file.type === "application/pdf" || PDF_EXT.some((ext) => name.endsWith(ext))) {
    return null;
  }
  return "Upload a PDF file.";
}

export function hasServerHotspotContent(url: string | undefined): boolean {
  if (!url || !String(url).trim()) return false;
  return !String(url).trim().startsWith("data:");
}

export function isDataUrlContent(url: string | undefined): boolean {
  return Boolean(url && String(url).trim().startsWith("data:"));
}
